import { getGoogleMapsApiKey } from "../config/googleMaps";
import { Shop } from "../types/shop";
import {
  DistanceStatus,
  DistanceResult,
  GenericDistanceResult,
  DistanceMatrixResponse,
  Coordinate,
  CoordinateWithId,
} from "../types/distance";
import {
  calculateAerialDistance,
  getClosestDestinationsByAerialDistance,
} from "../utils/distanceCalculations";

// Constants
const DEFAULT_CLOSEST_LIMIT = 5;
const API_UNITS = "imperial";
const API_MODE = "driving";

// Extend the global Window interface to include google
declare global {
  interface Window {
    google: any;
  }
}

// Simple rate limiting constants - just to prevent infinite loops
const MAX_REQUESTS_PER_SECOND = 3; // Allow 3 requests per second max
const RATE_LIMIT_WINDOW_MS = 1000; // 1 second window

// Re-export types for backward compatibility
export type {
  DistanceStatus,
  DistanceResult,
  GenericDistanceResult,
  DistanceMatrixResponse,
  Coordinate,
  CoordinateWithId,
};

// Rate limiter class
class RateLimiter {
  private requests: number[] = [];

  canMakeRequest(): boolean {
    const now = Date.now();

    // Clean old requests outside the time window
    this.requests = this.requests.filter(
      (time) => now - time < RATE_LIMIT_WINDOW_MS
    );

    // Check if we can make a request (allow 3 per second max)
    return this.requests.length < MAX_REQUESTS_PER_SECOND;
  }

  recordRequest(): void {
    const now = Date.now();
    this.requests.push(now);
  }

  getTimeUntilNextRequest(): number {
    if (this.requests.length === 0) return 0;

    const oldestRequest = Math.min(...this.requests);
    const timeUntilReset = RATE_LIMIT_WINDOW_MS - (Date.now() - oldestRequest);
    return Math.max(0, timeUntilReset);
  }
}

export class DistanceService {
  private readonly apiKey: string;
  private readonly baseUrl =
    "https://maps.googleapis.com/maps/api/distancematrix/json";
  private readonly rateLimiter: RateLimiter;

  constructor() {
    this.apiKey = getGoogleMapsApiKey();
    this.rateLimiter = new RateLimiter();
    this.log("Initialized", {
      apiKeyPresent: !!this.apiKey,
      apiKeyLength: this.apiKey?.length || 0,
      apiKeyPreview: this.apiKey
        ? this.apiKey.substring(0, 10) + "..."
        : "No key",
    });
  }

  // Private helper methods
  private log(message: string, data?: any): void {
    console.log(`[DistanceService] ${message}`, data || "");
  }

  private isGoogleMapsLoaded = false;
  private googleMapsLoadPromise: Promise<void> | null = null;

  // Request cache to prevent duplicate API calls
  private requestCache = new Map<string, Promise<DistanceMatrixResponse>>();
  private cacheTimeout = 30000; // 30 seconds cache

  private initializeGoogleMaps(): void {
    this.googleMapsLoadPromise = new Promise((resolve, reject) => {
      // Check if Google Maps is already loaded
      if (window.google && window.google.maps) {
        this.isGoogleMapsLoaded = true;
        resolve();
        return;
      }

      // Wait for Google Maps to load
      const checkGoogleMaps = () => {
        if (window.google && window.google.maps) {
          this.isGoogleMapsLoaded = true;
          resolve();
        } else {
          setTimeout(checkGoogleMaps, 100);
        }
      };

      // Start checking after a short delay
      setTimeout(checkGoogleMaps, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        if (!this.isGoogleMapsLoaded) {
          reject(new Error("Google Maps failed to load within 10 seconds"));
        }
      }, 10000);
    });
  }

  private async waitForGoogleMaps(): Promise<void> {
    if (!this.googleMapsLoadPromise) {
      this.initializeGoogleMaps();
    }
    if (!this.googleMapsLoadPromise) {
      throw new Error("Google Maps not initialized");
    }
    await this.googleMapsLoadPromise;
  }

  private generateCacheKey(
    origin: Coordinate,
    destinations: CoordinateWithId[]
  ): string {
    const originKey = `${origin.latitude.toFixed(6)},${origin.longitude.toFixed(
      6
    )}`;
    const destKeys = destinations
      .map((dest) => `${dest.latitude.toFixed(6)},${dest.longitude.toFixed(6)}`)
      .sort()
      .join("|");
    return `distance_${originKey}_${destKeys}`;
  }

  private async makeGoogleMapsRequest(
    origin: Coordinate,
    destinations: CoordinateWithId[]
  ): Promise<DistanceMatrixResponse> {
    // Generate cache key
    const cacheKey = this.generateCacheKey(origin, destinations);

    // Check if we have a cached request
    if (this.requestCache.has(cacheKey)) {
      this.log("Using cached request", { cacheKey });
      return this.requestCache.get(cacheKey)!;
    }

    this.log("Making Google Maps JavaScript API request", {
      origin: this.formatCoordinate(origin),
      destinationCount: destinations.length,
      cacheKey,
    });

    await this.waitForGoogleMaps();

    // Prepare origins and destinations for Google Maps API
    const origins = [{ lat: origin.latitude, lng: origin.longitude }];
    const googleDestinations = destinations.map((dest) => ({
      lat: dest.latitude,
      lng: dest.longitude,
    }));

    // Create the request
    const request = {
      origins: origins,
      destinations: googleDestinations,
      travelMode: window.google.maps.TravelMode.DRIVING,
      unitSystem: window.google.maps.UnitSystem.IMPERIAL,
      avoidHighways: false,
      avoidTolls: false,
    };

    // Create the request promise and cache it
    const requestPromise = this.makeDistanceMatrixRequestWithTimeout(request);
    this.requestCache.set(cacheKey, requestPromise);

    // Clean up cache after timeout
    setTimeout(() => {
      this.requestCache.delete(cacheKey);
    }, this.cacheTimeout);

    const response = await requestPromise;

    this.log("Google Maps API response received", {
      status: response.status,
      rowsCount: response.rows.length,
      elementsCount: response.rows[0]?.elements?.length || 0,
    });

    return response;
  }

  private makeDistanceMatrixRequestWithTimeout(
    request: any,
    timeoutMs: number = 10000
  ): Promise<DistanceMatrixResponse> {
    return new Promise((resolve, reject) => {
      const service = new window.google.maps.DistanceMatrixService();

      // Set up timeout
      const timeoutId = setTimeout(() => {
        reject(
          new Error(`Google Maps API request timed out after ${timeoutMs}ms`)
        );
      }, timeoutMs);

      service.getDistanceMatrix(request, (response: any, status: string) => {
        clearTimeout(timeoutId); // Clear timeout on response

        this.log("Raw Google Maps response", {
          status,
          responseKeys: Object.keys(response || {}),
          response: response,
        });

        if (status === window.google.maps.DistanceMatrixStatus.OK) {
          // Convert Google Maps JavaScript API response to our expected format
          const convertedResponse: DistanceMatrixResponse = {
            status: response.status || "OK",
            origin_addresses: response.originAddresses || [],
            destination_addresses: response.destinationAddresses || [],
            rows: response.rows || [],
          };

          this.log("Converted Google Maps response", {
            originalStatus: response.status,
            convertedStatus: convertedResponse.status,
            rowsCount: convertedResponse.rows.length,
            elementsCount: convertedResponse.rows[0]?.elements?.length || 0,
          });

          resolve(convertedResponse);
        } else {
          reject(new Error(`Google Maps API error: ${status}`));
        }
      });
    });
  }

  private makeDistanceMatrixRequest(request: any): Promise<any> {
    return this.makeDistanceMatrixRequestWithTimeout(request);
  }

  private logError(message: string, error: any): void {
    console.error(`[DistanceService] ${message}`, error);
  }

  private buildApiUrl(
    origin: Coordinate,
    destinations: CoordinateWithId[]
  ): string {
    const origins = `${origin.latitude},${origin.longitude}`;
    const destinationsStr = destinations
      .map((dest) => `${dest.latitude},${dest.longitude}`)
      .join("|");

    return `${this.baseUrl}?origins=${origins}&destinations=${destinationsStr}&key=${this.apiKey}&units=${API_UNITS}&mode=${API_MODE}`;
  }

  private processApiResponse(
    data: DistanceMatrixResponse,
    destinations: CoordinateWithId[]
  ): GenericDistanceResult[] {
    if (data.status !== "OK") {
      this.log(`API returned non-OK status: ${data.status}`);
      return [];
    }

    const results: GenericDistanceResult[] = [];
    const row = data.rows[0]; // We only have one origin

    destinations.forEach((dest, index) => {
      const element = row.elements[index];
      const status = element.status as DistanceStatus;

      if (element.status === "OK" && element.distance && element.duration) {
        results.push({
          id: dest.id,
          distance: {
            text: element.distance.text,
            value: element.distance.value,
          },
          duration: {
            text: element.duration.text,
            value: element.duration.value,
          },
          status: "OK",
        });
      } else {
        results.push({
          id: dest.id,
          distance: { text: "N/A", value: 0 },
          duration: { text: "N/A", value: 0 },
          status: status === "OK" ? "NOT_FOUND" : status,
        });
      }
    });

    return results;
  }

  private formatCoordinate(coord: Coordinate): string {
    return `${coord.latitude},${coord.longitude}`;
  }

  private formatDistance(meters: number): string {
    const miles = meters / 1609.34;
    if (miles < 0.1) {
      return `${Math.round(meters)}m`;
    }
    return `${miles.toFixed(2)}mi`;
  }

  /**
   * Simple rate limiting - just prevents rapid-fire requests
   * @returns Promise that resolves when we can make a request
   */
  private async waitForRateLimit(): Promise<void> {
    if (this.rateLimiter.canMakeRequest()) {
      return;
    }

    const timeUntilNext = this.rateLimiter.getTimeUntilNextRequest();

    this.log("Rate limit reached, waiting briefly", {
      timeUntilNext: `${Math.ceil(timeUntilNext / 1000)}s`,
    });

    // Just wait a short time to prevent rapid-fire requests
    if (timeUntilNext > 0) {
      await new Promise((resolve) => setTimeout(resolve, timeUntilNext));
    }
  }

  /**
   * Calculate driving distances with rate limiting protection
   * @param origin - Origin coordinates
   * @param shops - Array of shops with coordinates
   * @returns Promise with distance results for each shop
   */
  async calculateDrivingDistancesToShops(
    origin: Coordinate,
    shops: Shop[]
  ): Promise<DistanceResult[]> {
    this.log("Starting driving distance calculation with rate limiting", {
      origin: this.formatCoordinate(origin),
      shopCount: shops.length,
      shopIds: shops.map((s) => s.id),
    });

    try {
      if (shops.length === 0) {
        this.log("No shops provided, returning empty array");
        return [];
      }

      // Check rate limit before making request
      await this.waitForRateLimit();

      // Convert shops to coordinate format
      const destinations: CoordinateWithId[] = shops.map((shop) => ({
        id: shop.id.toString(),
        latitude: shop.latitude,
        longitude: shop.longitude,
      }));

      // Get generic distance results
      const genericResults = await this.calculateDistancesToCoordinates(
        origin,
        destinations
      );

      // Convert back to shop-specific format
      const results = genericResults.map((result) => {
        const shop = shops.find((s) => s.id.toString() === result.id);
        if (!shop) {
          throw new Error(`Shop with id ${result.id} not found`);
        }
        return {
          shopId: shop.id,
          shop: shop,
          distance: result.distance,
          duration: result.duration,
          status: result.status,
        };
      });

      const successfulResults = results.filter((r) => r.status === "OK").length;
      this.log("Driving distance calculation completed", {
        resultsCount: results.length,
        successfulResults,
        failedResults: results.length - successfulResults,
        sampleResults: results.slice(0, 3), // Log first 3 results for debugging
      });

      return results;
    } catch (error) {
      this.logError("Error calculating driving distances to shops", error);
      throw error;
    }
  }

  /**
   * Generic method to calculate distances from origin to multiple destinations
   * @param origin - Origin coordinates
   * @param destinations - Array of destination coordinates with IDs
   * @returns Promise with distance results for each destination
   */
  async calculateDistancesToCoordinates(
    origin: Coordinate,
    destinations: CoordinateWithId[]
  ): Promise<GenericDistanceResult[]> {
    const startTime = Date.now();

    this.log("Starting distance calculation", {
      origin: this.formatCoordinate(origin),
      destinationCount: destinations.length,
    });

    try {
      if (destinations.length === 0) {
        this.log("No destinations provided, returning empty array");
        return [];
      }

      // Record the request in rate limiter
      this.rateLimiter.recordRequest();

      // Use Google Maps JavaScript API (no CORS issues)
      const data = await this.makeGoogleMapsRequest(origin, destinations);

      this.log("API response data received", {
        status: data.status,
        originAddresses: data.origin_addresses,
        destinationAddresses: data.destination_addresses,
        rowsCount: data.rows.length,
        elementsCount: data.rows[0]?.elements?.length || 0,
      });

      const results = this.processApiResponse(data, destinations);

      const totalTime = Date.now() - startTime;
      const successfulResults = results.filter((r) => r.status === "OK").length;
      this.log(`Distance calculation completed in ${totalTime}ms`, {
        resultsCount: results.length,
        successfulResults,
        failedResults: results.length - successfulResults,
        sampleResult: results[0], // Log first result for debugging
      });

      return results;
    } catch (error) {
      const totalTime = Date.now() - startTime;
      this.logError(`Error calculating distances after ${totalTime}ms`, error);

      if (error instanceof Error) {
        this.logError("API request failed", error);
      }

      throw error;
    }
  }

  /**
   * Calculate distances from a vehicle location to multiple shops
   * @param vehicleLocation - Vehicle coordinates
   * @param shops - Array of shops with coordinates
   * @returns Promise with distance results for each shop
   */
  async calculateDistancesToShops(
    vehicleLocation: Coordinate,
    shops: Shop[]
  ): Promise<DistanceResult[]> {
    this.log("Starting shop distance calculation", {
      vehicleLocation: this.formatCoordinate(vehicleLocation),
      shopCount: shops.length,
      shopIds: shops.map((s) => s.id),
    });

    try {
      if (shops.length === 0) {
        this.log("No shops provided, returning empty array");
        return [];
      }

      // Convert shops to coordinate format
      const destinations: CoordinateWithId[] = shops.map((shop) => ({
        id: shop.id.toString(),
        latitude: shop.latitude,
        longitude: shop.longitude,
      }));

      // Get generic distance results
      const genericResults = await this.calculateDistancesToCoordinates(
        vehicleLocation,
        destinations
      );

      // Convert back to shop-specific format
      const results = genericResults.map((result) => {
        const shop = shops.find((s) => s.id.toString() === result.id);
        if (!shop) {
          throw new Error(`Shop with id ${result.id} not found`);
        }
        return {
          shopId: shop.id,
          shop: shop,
          distance: result.distance,
          duration: result.duration,
          status: result.status,
        };
      });

      const successfulResults = results.filter((r) => r.status === "OK").length;
      this.log("Shop distance calculation completed", {
        resultsCount: results.length,
        successfulResults,
        failedResults: results.length - successfulResults,
      });

      return results;
    } catch (error) {
      this.logError("Error calculating distances to shops", error);
      throw error;
    }
  }

  /**
   * Calculate distance to a single shop
   * @param vehicleLocation - Vehicle coordinates
   * @param shop - Shop with coordinates
   * @returns Promise with distance result
   */
  async calculateDistanceToShop(
    vehicleLocation: Coordinate,
    shop: Shop
  ): Promise<DistanceResult> {
    this.log("Starting single shop distance calculation", {
      vehicleLocation: this.formatCoordinate(vehicleLocation),
      shopId: shop.id,
      shopCoords: this.formatCoordinate(shop),
    });

    const results = await this.calculateDistancesToShops(vehicleLocation, [
      shop,
    ]);
    const result = results[0];

    this.log("Single shop distance calculation completed", {
      shopId: shop.id,
      distance: result.distance.text,
      duration: result.duration.text,
      status: result.status,
    });

    return result;
  }

  /**
   * Get the N closest destinations based on aerial distance (for pre-filtering)
   * This is a quick calculation to reduce API calls
   * @param origin - Origin coordinates
   * @param destinations - Array of destinations with coordinates
   * @param limit - Number of closest destinations to return
   * @returns Top N closest destinations
   */
  getClosestDestinationsByAerialDistance(
    origin: Coordinate,
    destinations: CoordinateWithId[],
    limit: number = DEFAULT_CLOSEST_LIMIT
  ): Array<CoordinateWithId & { aerialDistance: number }> {
    this.log("Calculating aerial distances for pre-filtering", {
      origin: this.formatCoordinate(origin),
      destinationCount: destinations.length,
      limit,
    });

    // Delegate to pure function
    const result = getClosestDestinationsByAerialDistance(
      origin,
      destinations,
      limit
    );

    this.log("Aerial distance calculation completed", {
      resultCount: result.length,
      closestDistance: result[0]?.aerialDistance
        ? this.formatDistance(result[0].aerialDistance)
        : "N/A",
      farthestDistance: result[result.length - 1]?.aerialDistance
        ? this.formatDistance(result[result.length - 1].aerialDistance)
        : "N/A",
    });

    return result;
  }

  /**
   * Get the N closest shops based on aerial distance (for pre-filtering)
   * This is a quick calculation to reduce API calls
   * @param vehicleLocation - Vehicle coordinates
   * @param shops - Array of shops
   * @param limit - Number of closest shops to return
   * @returns Top N closest shops
   */
  getClosestShopsByAerialDistance(
    vehicleLocation: Coordinate,
    shops: Shop[],
    limit: number = DEFAULT_CLOSEST_LIMIT
  ): Array<Shop & { aerialDistance: number }> {
    this.log("Calculating aerial distances for shops pre-filtering", {
      vehicleLocation: this.formatCoordinate(vehicleLocation),
      shopCount: shops.length,
      limit,
    });

    const destinations: CoordinateWithId[] = shops.map((shop) => ({
      id: shop.id.toString(),
      latitude: shop.latitude,
      longitude: shop.longitude,
    }));

    const closestDestinations = this.getClosestDestinationsByAerialDistance(
      vehicleLocation,
      destinations,
      limit
    );

    // Map back to shops with aerial distances
    const result = closestDestinations.map((dest) => {
      const shop = shops.find((s) => s.id.toString() === dest.id);
      if (!shop) {
        throw new Error(`Shop with id ${dest.id} not found`);
      }
      return { ...shop, aerialDistance: dest.aerialDistance };
    });

    this.log("Shop aerial distance calculation completed", {
      resultCount: result.length,
      shopIds: result.map((s) => s.id),
      closestShopId: result[0]?.id,
      closestDistance: result[0]?.aerialDistance
        ? this.formatDistance(result[0].aerialDistance)
        : "N/A",
    });

    return result;
  }


  /**
   * Clear the request cache (useful for testing or memory management)
   */
  clearCache(): void {
    this.requestCache.clear();
    this.log("Request cache cleared");
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.requestCache.size,
      keys: Array.from(this.requestCache.keys()),
    };
  }
}

// Export singleton instance
export const distanceService = new DistanceService();
